import marketingSeo from './marketingSeo.json';
import { BLOG_POSTS } from '../content/blogPosts';
import { LANDING_FAQS } from '../content/landingFeatures';

export const MARKETING_ORIGIN = marketingSeo.origin;
export const MARKETING_APP_ORIGIN = marketingSeo.appOrigin;
export const MARKETING_SITE_NAME = marketingSeo.siteName;
export const DEFAULT_OG_IMAGE = marketingSeo.defaultOgImage;

const BLOG_ROUTE_ENTRIES = Object.fromEntries(
  BLOG_POSTS.map((post) => [
    `/blog/${post.slug}`,
    {
      title: post.seoTitle,
      description: post.description,
      ogType: 'article',
      jsonLd: ['organization'],
    },
  ]),
);

export const MARKETING_ROUTES = {
  ...marketingSeo.routes,
  '/blog': {
    title: 'Workforce Management Blog | Unified Workforce',
    description:
      'Guides on employee time tracking, remote team attendance, hybrid workforce operations, leave management, and payroll-ready workforce records.',
    ogType: 'website',
    jsonLd: ['organization', 'webSite'],
  },
  ...BLOG_ROUTE_ENTRIES,
};

export const MARKETING_PUBLIC_PATHS = Object.keys(MARKETING_ROUTES);

export const isMarketingPublicPath = (pathname) => {
  if (MARKETING_PUBLIC_PATHS.includes(pathname)) return true;
  if (pathname === '/wiki' || pathname.startsWith('/wiki/')) return true;
  return pathname.startsWith('/blog/') && BLOG_POSTS.some((post) => pathname === `/blog/${post.slug}`);
};

export const getMarketingSeo = (pathname) => MARKETING_ROUTES[pathname] || null;

export const absoluteMarketingUrl = (pathname = '/') => {
  const normalized = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return `${MARKETING_ORIGIN}${normalized === '/' ? '/' : normalized}`;
};

export const absoluteOgImageUrl = (imagePath = DEFAULT_OG_IMAGE) => {
  const normalized = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
  return `${MARKETING_ORIGIN}${normalized}`;
};

export const buildOrganizationJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: MARKETING_SITE_NAME,
  url: MARKETING_ORIGIN,
  logo: absoluteOgImageUrl('/logo512.png'),
  sameAs: [],
});

export const buildSoftwareApplicationJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: MARKETING_SITE_NAME,
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web, Windows, macOS, Linux',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
    description: 'Free plan available',
  },
  description: MARKETING_ROUTES['/'].description,
  url: MARKETING_ORIGIN,
});

export const buildWebSiteJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: MARKETING_SITE_NAME,
  url: MARKETING_ORIGIN,
});

export const buildFaqPageJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: LANDING_FAQS.map(([question, answer]) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: answer,
    },
  })),
});

export const buildJsonLdForRoute = (pathname) => {
  const route = getMarketingSeo(pathname);
  if (!route?.jsonLd?.length) return [];

  const builders = {
    organization: buildOrganizationJsonLd,
    softwareApplication: buildSoftwareApplicationJsonLd,
    webSite: buildWebSiteJsonLd,
    faqPage: buildFaqPageJsonLd,
  };

  return route.jsonLd
    .map((key) => builders[key]?.())
    .filter(Boolean);
};
